import { max as MathMax, min as MathMin, round } from "lodash-es";
import { type FC, type FocusEvent, type KeyboardEventHandler, useCallback, useMemo } from "react";
import { NumericFormat } from "react-number-format";

import { ActionButton, ActionButtonIntent, ActionButtonSize, HeaderBar, Slider } from "widgets";
import { getPrecision } from "widgets/utils";

import { type IColumnAnnotationFilter } from "../../redux/modules/dataset-viewer.js";
import { type IConditionValuesFilterProps } from "./CategoricalConditionValuesFilter.js";

/**
 * Component representing the filter options and histogram of a condition/annotation.
 */
const NumericalConditionValuesFilter: FC<IConditionValuesFilterProps> = (props) => {
  const [min, max] = useMemo<[number, number]>(() => {
    const min = MathMin(props.condition.valueSet?.map((value) => Number(value))) ?? 0;
    const max = MathMax(props.condition.valueSet?.map((value) => Number(value))) ?? 1;

    return [min, max];
  }, [props.condition.valueSet]);
  const precision = useMemo(() => Math.max(getPrecision(min), getPrecision(max)), [max, min]);

  const [minSelected, maxSelected] = (() => {
    const conditionFilter = props.filteredConditions[props.condition.field ?? 0];
    const minSelected = conditionFilter?.minNumericalValue || min;
    const maxSelected = conditionFilter?.maxNumericalValue || max;

    return [minSelected, maxSelected];
  })();

  const reset = useCallback(() => {
    props.filteredConditionsChanged(props.condition.field, {
      name: props.condition.field,
      minNumericalValue: null,
      maxNumericalValue: null,
    });
  }, [props]);

  const onChange = useCallback(
    ([minVal, maxVal]: number[]) => {
      const setRange = (minSelection: number, maxSelection: number) => {
        const columnAnnotationFilter: IColumnAnnotationFilter = { ...props.filteredConditions[props.condition.field] };
        columnAnnotationFilter.minNumericalValue = minSelection === min ? null : minSelection;
        columnAnnotationFilter.maxNumericalValue = maxSelection === max ? null : maxSelection;
        // tell the redux state
        props.filteredConditionsChanged(props.condition.field, columnAnnotationFilter);
      };

      const minRange = precision === 0 ? Math.floor(minVal) : round(minVal, precision);
      const maxRange = precision === 0 ? Math.floor(maxVal) : round(maxVal, precision);
      setRange(minRange, maxRange);
    },
    [max, min, precision, props],
  );

  const cleanupAndConvertToNumber = (value: string): number => Number.parseFloat(value.replace(/,/g, ""));

  const setMinValue = (value: string) => {
    let newValue = cleanupAndConvertToNumber(value);

    newValue = Math.max(newValue, min);
    newValue = Math.min(newValue, maxSelected);

    onChange([newValue, maxSelected]);
  };

  const setMaxValue = (value: string) => {
    let newValue = cleanupAndConvertToNumber(value);

    newValue = Math.max(newValue, minSelected);
    newValue = Math.min(newValue, max);

    onChange([minSelected, newValue]);
  };

  const handleMinKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter") {
      setMinValue(event.currentTarget.value);
    }
  };

  const handleMaxKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === "Enter") {
      setMaxValue(event.currentTarget.value);
    }
  };

  return (
    <div className="chart-filters-item-values-popover">
      <HeaderBar
        title={props.condition.name}
        actions={[
          <ActionButton
            key={"reset"}
            label={"Reset"}
            onPress={reset}
            size={ActionButtonSize.Small}
            intent={ActionButtonIntent.Outlined}
          />,
        ]}
      />

      <div className="chart-filters-item-values" style={{ paddingTop: 20 }}>
        <Slider
          value={[minSelected, maxSelected]}
          minValue={min}
          maxValue={max}
          onChange={onChange}
          tooltip={(value) =>
            value.toLocaleString("en-US", { minimumFractionDigits: precision, maximumFractionDigits: precision })
          }
          label={`from ${min.toLocaleString("en-US", { maximumFractionDigits: precision })} to ${max.toLocaleString(
            "en-US",
            {
              maximumFractionDigits: precision,
            },
          )}`}
          noHeader
        />
        <div style={{ marginTop: 5 }}>
          <NumericFormat
            value={minSelected}
            onKeyPress={handleMinKeyPress}
            displayType={"input"}
            thousandSeparator
            className="form-control"
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              setMinValue(e.currentTarget.value);
            }}
            style={{ width: 70, display: "inline", marginRight: 10 }}
          />
          <NumericFormat
            value={maxSelected}
            onKeyPress={handleMaxKeyPress}
            displayType={"input"}
            thousandSeparator
            key="maxValue"
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              setMaxValue(e.currentTarget.value);
            }}
            className="form-control"
            style={{ width: 70, display: "inline", float: "right" }}
          />
        </div>
      </div>
    </div>
  );
};

export default NumericalConditionValuesFilter;
